import * as React from "react"
import {
  useIntl,
  FormattedMessage,
  FormattedNumber,
  FormattedTime,
} from "react-intl"
import styled from "styled-components"
import {border, layout, typography} from "styled-system"

import numeral from "numeral"
import "numeral/locales"

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faCheckCircle} from "@fortawesome/pro-regular-svg-icons/faCheckCircle"
import {faCheckSquare} from "@fortawesome/pro-regular-svg-icons/faCheckSquare"
import {faCommentsAlt} from "@fortawesome/pro-regular-svg-icons/faCommentsAlt"
import {faCopy} from "@fortawesome/pro-regular-svg-icons/faCopy"
import {faCreditCard} from "@fortawesome/pro-regular-svg-icons/faCreditCard"
import {faEnvelopeOpenText} from "@fortawesome/pro-regular-svg-icons/faEnvelopeOpenText"
import {faFileSignature} from "@fortawesome/pro-regular-svg-icons/faFileSignature"
import {faSquare} from "@fortawesome/pro-regular-svg-icons/faSquare"
import {faArrowRight} from "@fortawesome/pro-solid-svg-icons/faArrowRight"
import {faCheckCircle as faCheckCircleSolid} from "@fortawesome/pro-solid-svg-icons/faCheckCircle"
import {faEllipsisH} from "@fortawesome/pro-solid-svg-icons/faEllipsisH"

import {Dialog} from "@reach/dialog"
import {Menu, MenuList, MenuButton, MenuItem} from "@reach/menu-button"

import NotificationsContext from "../../../contexts/notifications.js"

import useDebounce from "../../../hooks/debounce.js"
import useFetcher from "../../../hooks/fetcher.js"

import {Content as DialogContent} from "../../../components/dialog.js"
import Ellipsis from "../../../components/ellipsis.js"
import {StyledButton} from "../../../components/styled.js"
import Text from "../../../components/text.js"

import BankAuthState from "./bank-auth-state.js"
import DebitButton from "./debit-button.js"
import DGCScansState from "./dgc-scans-state.js"
import SendMessage from "./send-message.js"
import {
  StyledInput,
  StyledTr,
  iconFloatingStyles,
} from "../../../components/styled.js"

const StyledAvatar = styled.img.attrs({
  size: "input",
  borderRadius: "round",
})`
  ${border} ${layout}
  ${props => (props.GPLink ? "cursor: pointer;" : null)}
`
const StyledPriceInput = styled(StyledInput).attrs({
  textAlign: "right",
  width: "price-input",
  height: "27px",
  lineHeight: "27px",
  px: 1,
  mr: 1,
})`
  ${layout} ${typography}
`

const CancelButton = styled(StyledButton).attrs({backgroundColor: "warning"})``

const now = new Date()

const Stay = props => {
  const {id, clientID, row, config} = props
  const {addError, addInfo, addSuccess} = React.useContext(NotificationsContext)

  const intl = useIntl()
  const [bankAuthURL, setBankAuthURL] = React.useState(null)
  const bankAuthdata = useFetcher(bankAuthURL, {nocache: true})

  const [releaseBankAuthURL, setReleaseBankAuthURL] = React.useState(null)
  const bankAuthReleasedata = useFetcher(releaseBankAuthURL, {nocache: true})

  const [copyLinkURL, setCopyLinkURL] = React.useState(null)
  const copyLinkData = useFetcher(copyLinkURL, {nocache: true})

  const [doneURL, setDoneURL] = React.useState(null)
  const doneData = useFetcher(doneURL, {nocache: true})

  const [priceRef, setPriceRef] = React.useState()
  const [price, setPrice] = React.useState(row?.bank_auth_value?.value ?? null)
  const [debouncedPrice] = useDebounce(price, 500)

  const [bankDebitShortcut, setBankDebitShortcut] = React.useState(
    row?.bank_debit_shortcut
  )
  const [bankAuthValueEditable, setBankAuthValueEditable] = React.useState(
    row?.bank_auth_value?.editable
  )

  const [askConfirm, setAskConfirm] = React.useState(false)
  const open = () => setAskConfirm(true)
  const close = () => setAskConfirm(false)

  const formatPrice = price =>
    intl
      .formatNumber(price, {
        style: "currency",
        currency: config.currency.code,
        currencyDisplay: "code",
      })
      .replace(/[a-z]{3}/i, "")
      .trimEnd()

  React.useEffect(() => {
    if (!intl?.locale) return
    numeral.locale(intl.locale === "en" ? "en-gb" : intl.locale)
  }, [intl.locale])

  React.useEffect(() => {
    if (!priceRef) return
    if (price === null || price === undefined) return
    priceRef.value = formatPrice(price)
  }, [priceRef, price])

  React.useEffect(() => {
    if ((debouncedPrice ?? false) === false) return
    if (
      debouncedPrice !== row?.bank_auth_value?.value ||
      bankAuthdata.fetched
    ) {
      setBankAuthURL(
        `home/clients/${clientID}/arrivals/setBankAuthValue?id=${id}&bank_auth_value=${debouncedPrice}`
      )
    }
  }, [debouncedPrice])

  React.useEffect(() => {
    if (bankAuthdata?.error) {
      addError(<FormattedMessage id="bank-auth.notification.error" />)
    }
    if (!bankAuthdata?.data?.bank_auth_value) return
    if (!bankAuthdata?.fetched) return
    addSuccess(<FormattedMessage id="bank-auth.notification.success" />)
  }, [bankAuthdata])

  React.useEffect(() => {
    if (!bankAuthReleasedata?.data) return
    if (bankAuthReleasedata?.error || !bankAuthReleasedata?.data?.canceled) {
      addError(<FormattedMessage id="release-bank-auth.notification.error" />)
    } else {
      setBankDebitShortcut("unavailable")
      setBankAuthValueEditable(false)
      addSuccess(
        <FormattedMessage id="release-bank-auth.notification.success" />
      )
    }
    close()
  }, [bankAuthReleasedata])

  React.useEffect(() => {
    if (copyLinkData?.loading) {
      addInfo(<FormattedMessage id="copy-link.notification.loading" />)
      return
    }
    if (copyLinkData?.error) {
      addError(<FormattedMessage id="copy-link.notification.error" />)
      return
    }
    if (!copyLinkData?.fetched) return //  so we do not add a notification for another already fetched link
    if (copyLinkData.data.link === false) {
      addError(<FormattedMessage id="copy-link.notification.error" />)
      return
    }
    addSuccess(
      <Text ellipsis>
        <FormattedMessage id="copy-link.notification.success" />
        <a href={copyLinkData.data.link} target="_blank" rel="noreferrer">
          <FormattedMessage id="copy-link.notification.success-click-here" />
        </a>
      </Text>
    )
    window.copy(copyLinkData.data.link)
  }, [copyLinkData])

  const handlePriceBlur = event => {
    setPrice(numeral(event.target.value).value())
  }

  const handleDone = () => {
    setDoneURL(
      `home/clients/${clientID}/arrivals/toggleDone?id=${id}&done=${Number(
        !row.done
      )}`
    )
    row.done = !row.done
  }

  const handleWithdraw = () => {
    setBankDebitShortcut("completed")
    setBankAuthValueEditable(false)
  }

  const handleReleaseBankAuth = () => {
    setReleaseBankAuthURL(
      `home/clients/${clientID}/arrivals/cancelAuthorization?id=${id}`
    )
  }

  React.useEffect(() => {
    if (row?.bank_auth_value?.value) {
      if (!bankAuthdata?.fetched) {
        setPrice(row?.bank_auth_value?.value)
      }
    }

    if (row?.bank_auth_value?.editable !== undefined) {
      setBankAuthValueEditable(row?.bank_auth_value?.editable)
    }
  }, [row])

  React.useEffect(() => {
    if (doneData?.loading || !doneData?.fetched) return
    if (doneData?.error) {
      addError(<FormattedMessage id="done.notification.error" />)
      return
    }
    if (doneData.data) {
      if (doneData.data.done) {
        addSuccess(<FormattedMessage id="done.notification.success-done" />)
      } else {
        addSuccess(<FormattedMessage id="done.notification.success-not-done" />)
      }
      return
    }
    addError(<FormattedMessage id="done.notification.error" />)
  }, [doneData])

  return (
    <>
      {askConfirm && (
        <Dialog onDismiss={close} aria-labelledby={id}>
          <DialogContent
            title={<FormattedMessage id="release-bank-auth.alert" />}
            close={close}
            buttons={
              <>
                <StyledButton onClick={handleReleaseBankAuth}>OK</StyledButton>
                <CancelButton onClick={() => setAskConfirm(false)} ml={3}>
                  <FormattedMessage id="alert.cancel" />
                </CancelButton>
              </>
            }
          ></DialogContent>
        </Dialog>
      )}
      <StyledTr key={id}>
        {Object.keys(config.columns).map(column => {
          const key = `${id}-${column}`
          const cell = row[column]
          const value = cell?.value ?? ""
          const GPLink = row["avatar"].value.link
          switch (column) {
            case "avatar":
              return (
                <td key={key}>
                  <StyledAvatar
                    src={value.image}
                    onClick={() => (GPLink ? window.goTo(GPLink) : undefined)}
                    GPLink={GPLink}
                  />
                </td>
              )
            case "name":
              return (
                <td key={key}>
                  <Text
                    color={GPLink ? "primary" : null}
                    onClick={() => (GPLink ? window.goTo(GPLink) : undefined)}
                    style={GPLink ? {cursor: "pointer"} : null}
                  >
                    {cell?.sortValue}
                  </Text>
                </td>
              )
            case "stay":
              return (
                <td key={key}>
                  <Ellipsis lines={4}>
                    <Text mr={1}>{value.arrival}</Text>
                    <FontAwesomeIcon icon={faArrowRight} />
                    <Text ml={1}>{value.departure}</Text>
                    <br />
                    <Text uppercase>{value.idresa}</Text>
                  </Ellipsis>
                </td>
              )

            case "bank_auth_value":
              return (
                <td key={key}>
                  {!bankAuthValueEditable ? (
                    <FormattedNumber
                      style="currency"
                      currency={config.currency.code}
                      value={price}
                    />
                  ) : (
                    <>
                      <StyledPriceInput
                        ref={setPriceRef}
                        onBlur={handlePriceBlur}
                      />
                      {config.currency.symbol}
                    </>
                  )}
                </td>
              )

            case "bank_auth_state":
              return (
                <td key={key}>
                  <BankAuthState cell={cell} />
                </td>
              )

            case "dgc_scans":
              if (!cell) {
                return <td key={key}>{null}</td>
              }
              return (
                <td key={key}>
                  <DGCScansState cell={cell} />
                </td>
              )

            case "police_form":
              if (!cell) {
                return (
                  <td key={key}>
                    <FontAwesomeIcon icon={faFileSignature} color="#bebebe" />
                  </td>
                )
              }
              return (
                <td key={key}>
                  <a
                    href={cell}
                    target="_blank"
                    rel="noreferrer"
                    style={{color: "#000"}}
                  >
                    <span className="fa-layers fa-fw">
                      <FontAwesomeIcon icon={faFileSignature} />
                      <FontAwesomeIcon
                        icon={faCheckCircleSolid}
                        size="sm"
                        color="#2abb9c"
                        style={iconFloatingStyles}
                      />
                    </span>
                  </a>
                </td>
              )

            case "prestay_form":
              if (!cell) {
                return (
                  <td key={key}>
                    <FontAwesomeIcon
                      icon={faEnvelopeOpenText}
                      color="#bebebe"
                    />
                  </td>
                )
              }
              return (
                <td key={key}>
                  <a
                    href={cell}
                    target="_blank"
                    rel="noreferrer"
                    style={{color: "#000"}}
                  >
                    <span className="fa-layers fa-fw">
                      <FontAwesomeIcon icon={faEnvelopeOpenText} />
                      <FontAwesomeIcon
                        icon={faCheckCircleSolid}
                        size="sm"
                        color="#2abb9c"
                        style={iconFloatingStyles}
                      />
                    </span>
                  </a>
                </td>
              )

            case "arrival_time":
              if (!value) return <td key={key}></td>
              return (
                <td key={key}>
                  <FormattedTime
                    value={
                      new Date(
                        now.getFullYear(),
                        now.getMonth(),
                        now.getDate(),
                        value.split(":")[0],
                        value.split(":")[1],
                        value.split(":")[2]
                      )
                    }
                  />
                </td>
              )

            case "bank_debit_shortcut":
              return (
                <td key={key} align="center">
                  {bankDebitShortcut ===
                  "unavailable" ? null : bankDebitShortcut === "completed" ? (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      size="lg"
                      color="#2abb9c"
                    />
                  ) : (
                    <DebitButton
                      id={id}
                      clientID={clientID}
                      bankAuthValue={row?.bank_auth_value.value}
                      currency={config.currency}
                      onWithdraw={handleWithdraw}
                    />
                  )}
                </td>
              )

            case "actions":
              if (!Array.isArray(cell) || cell.length === 0) {
                return <td key={key}></td>
              }
              return (
                <td key={key}>
                  <Menu>
                    <MenuButton as="span">
                      <FontAwesomeIcon icon={faEllipsisH} />
                    </MenuButton>
                    <MenuList>
                      {cell.map((action, index) => {
                        if (action.startsWith("send")) {
                          // send_message
                          return (
                            <SendMessage
                              id={id}
                              clientID={clientID}
                              key={`${key}-${action}-${index}`}
                              email={row?.email_address}
                              phone={row?.mobile_phone}
                              cannedMessages={config?.cannedMessages}
                              siteLang={config.lang}
                              guestLang={row?.lang ?? config.lang}
                            >
                              <FontAwesomeIcon
                                icon={faCommentsAlt}
                                style={{fontSize: "13px"}}
                              />
                              <Text nowrap ml={3}>
                                <FormattedMessage id={`arrivals.${action}`} />
                              </Text>
                            </SendMessage>
                          )
                        }
                        if (action.startsWith("copy")) {
                          // copy_police_form_link, copy_prestay_form_link, copy_checkin_link, copy_payment_link
                          return (
                            <MenuItem
                              key={`${key}-${action}-${index}`}
                              onSelect={() =>
                                setCopyLinkURL(
                                  `home/clients/${clientID}/arrivals/getLink?id=${id}&link=${action
                                    .replace("copy_", "")
                                    .replace("_link", "")}`
                                )
                              }
                            >
                              <FontAwesomeIcon
                                icon={faCopy}
                                style={{fontSize: "13px"}}
                              />
                              <Text nowrap ml={3}>
                                <FormattedMessage id={`arrivals.${action}`} />
                              </Text>
                            </MenuItem>
                          )
                        }
                        if (action.startsWith("release")) {
                          // release_bank_auth
                          return (
                            <MenuItem
                              key={`${key}-${action}-${index}`}
                              onSelect={open}
                            >
                              <FontAwesomeIcon
                                icon={faCreditCard}
                                style={{fontSize: "13px"}}
                              />
                              <Text nowrap ml={3}>
                                <FormattedMessage id={`arrivals.${action}`} />
                              </Text>
                            </MenuItem>
                          )
                        }
                      })}
                    </MenuList>
                  </Menu>
                </td>
              )

            case "done":
              return (
                <td key={key}>
                  <FontAwesomeIcon
                    icon={cell ? faCheckSquare : faSquare}
                    style={{fontSize: "16px"}}
                    onClick={handleDone}
                  />
                </td>
              )
            default:
              // console.log({column, cell})
              return (
                <td key={key}>
                  <Ellipsis lines={2}>{value}</Ellipsis>
                </td>
              )
          }
        })}
      </StyledTr>
    </>
  )
}

export default Stay
